import React from 'react'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { Grid, Box, Tooltip, Typography } from '@material-ui/core'
import { Formik, Field } from 'formik'
import clsx from 'clsx'
import StyledRadio from '@src/routes/Configurators/utils/ConfiguratorCustomRadioButton'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import RadioGroup from '@material-ui/core/RadioGroup'
import Button from '@material-ui/core/Button'
import { FormattedMessage, useIntl } from 'react-intl'
import messages from '../../../../messages'
import LiquidSelectAdapter from '@src/components/LiquidSelectAdapter'
import { SpecificationMessages, ActiveLinkType } from '../../enum'
import InfoIcon from '@material-ui/icons/Info'
import {
  useCustomCrisprTotalValidationMutation,
  useCustomCrisprSaveToSessionMutation,
} from '@src/mutations/ConfiguratorTubesMutation.generated'
import { useGetCrisprPriceCallLazyQuery } from '@src/queries/ConfiguratorTubesQuery.generated'
import { ConfigContext } from '../../Genomics'
import { genomicsRoute, useRouter } from '@src/routes'
import { dropDownValuesForCustomCrispr } from '@src/routes/Configurators/components/OptionalModifications/ModificationResponse'
import { cloneDeep } from 'lodash'
import Link from '@material-ui/core/Link'
import CircleLoader from '@src/components/CircleLoader'
import { ConfiguratorValues } from '@src/routes/Configurators/utils/constants'
import { checkValidKey } from '@src/routes/Configurators/utils/util'
import { SessionData } from '../../Reducer'
import PerformanceGuaranteeModal from '@src/routes/Configurators/components/PerformanceGuaranteeModal'
import GlobalAlert from '@src/components/GlobalAlert'
import CustomCrisprRecommendationModal from '@src/routes/Configurators/components/PopUp/CustomCrisprRecommendationModal'
import ErrorIcon from '@material-ui/icons/Error'

const useStyles = makeStyles((theme: Theme) => {
  return {
    linkStyle3: {
      color: theme.palette.primary.main,
    },
    vectorLabel: {
      fontSize: theme.typography.pxToRem(14),
    },
    vectorLabel2: {
      paddingBottom: theme.spacing(1),
    },
    nextButton: {
      fontSize: theme.typography.pxToRem(16),
      fontWeight: 'bold',
    },
    InfoPopup: {
      maxWidth: theme.spacing(150),
      backgroundColor: theme.palette.common.white,
      position: 'relative',
      border: `1px solid ${theme.palette.common.white}`,
      boxShadow: '1px 1px 20px 1px lightgrey',
      padding: theme.spacing(5),
      fontSize: theme.typography.pxToRem(12),
      color: '#1b1b25',
      borderRadius: '2%',
      marginTop: theme.spacing(1),
    },
    InfoPopup2: {
      maxWidth: theme.spacing(70),
      backgroundColor: theme.palette.common.white,
      position: 'relative',
      border: `1px solid ${theme.palette.common.white}`,
      boxShadow: '1px 1px 20px 1px lightgrey',
      padding: theme.spacing(5),
      fontSize: theme.typography.pxToRem(12),
      color: '#1b1b25',
      borderRadius: '2%',
      marginTop: theme.spacing(1),
    },
    arrow: {
      color: theme.palette.common.white,
    },
    InfoIcon: {
      position: 'relative',
      fontSize: theme.typography.pxToRem(16),
      top: theme.spacing(0.5),
      marginLeft: theme.spacing(1.5),
    },
    container: {
      border: `1px solid ${theme.palette.grey[400]}`,
      padding: theme.spacing(6, 6, 6, 6),
    },
    pageHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    title: {
      textTransform: 'uppercase',
    },
    requiredField: {
      fontSize: theme.typography.pxToRem(16),
      margin: theme.spacing(2, 0),
    },
    errorAlert: {
      width: 'fit-content',
      marginTop: theme.spacing(6),
    },
    fieldText: {
      fontSize: theme.typography.pxToRem(14),
      fontWeight: theme.typography.fontWeightBold,
    },
    font16: {
      fontSize: theme.typography.pxToRem(16),
    },
    labelFont: {
      padding: 0,
      '& .MuiTypography-body1': {
        fontSize: theme.typography.pxToRem(16),
      },
      '& .MuiFormControlLabel-root': {
        marginLeft: 0,
      },
    },
    radio: {
      padding: 0,
    },
    radioGroup: {
      gap: 8,
      marginTop: 8,
      '& .MuiFormControlLabel-root': {
        marginLeft: 0,
        marginRight: 0,
      },
    },
    marginTop24: {
      margin: theme.spacing(6, 0, 0, 0),
    },
    requiredFieldError: {
      color: theme.palette.error.main,
      marginTop: theme.spacing(1),
    },
    dry: {
      marginTop: theme.spacing(2),
      fontSize: theme.typography.pxToRem(16),
    },
    dropDown: {
      width: '332px',
    },
    errorIcon: {
      fontSize: theme.typography.pxToRem(12),
      marginRight: theme.spacing(2),
    },
    hasRequiredError: {
      color: theme.palette.error.main,
      '& .fieldText': {
        color: theme.palette.error.main,
      },
      '& .MuiRadio-colorSecondary:not(.Mui-disabled) > .MuiIconButton-label > span':
        {
          border: `2px solid ${theme.palette.error.main}`,
        },
    },
    purificationHelpText: {
      marginBottom: theme.spacing(2),
    },
  }
})

const CrisprSpecifications = (props) => {
  const { productDetails, specificationsData, setItemDetails } = props
  const router = useRouter()
  const index = 0
  const classes = useStyles()
  const intl = useIntl()
  const { state, dispatch } = React.useContext(ConfigContext)
  const { sessionData, currentSubModelIndex, customCrisprIndex } = state
  const [physicalMaterial, setPhysicalMaterial] = React.useState(
    sessionData[0].physicalMaterial || SpecificationMessages.Select
  )
  const [vectorDropdownValue, setVectorDropdownValue] = React.useState(
    sessionData[0].vector || SpecificationMessages.lv01
  )
  const [lentiviralDropdownValue, setLentiviralDropdownValue] = React.useState(
    sessionData[0].lentiviralBackbone || SpecificationMessages.lv01
  )
  const [amountDropdownValue, setAmountDropdownValue] = React.useState(
    sessionData[0].amount || SpecificationMessages.one
  )
  const [titerDropdownValue, setTiterDropdownValue] = React.useState(
    sessionData[0].viralTiter || SpecificationMessages.TenPSix
  )
  const [volumeDropdownValue, setVolumeDropdownValue] = React.useState(
    sessionData[0].volume || SpecificationMessages.dotTwo
  )
  const [aliquotingDropdownValue, setAliquotingDropdownValue] = React.useState(
    sessionData[0].aliquoting || SpecificationMessages.mul1x200
  )
  const reviewPageRoute = `${genomicsRoute.index()}?product=${
    productDetails.productType
  }&activeLink=${ActiveLinkType.ConfirmOrder}`
  const [reqPhysical, setReqPhysical] = React.useState(false)
  const [reqSpecies, setReqSpecies] = React.useState(false)
  const [reqScale, setReqScale] = React.useState(false)
  const [reqStructure, setReqStructure] = React.useState(false)
  const [reqMod, setReqMod] = React.useState(false)
  const [reqPuri, setReqPuri] = React.useState(false)
  const [open, setOpen] = React.useState(false)
  const [aliquotingFlag, setAliquotingFlag] = React.useState(false)
  const [volumeFlag, setVolumeFlag] = React.useState(false)
  const [checkForRadioButton, setCheckForRadioButton] = React.useState(false)
  const [loader, setLoader] = React.useState(false)

  const [customCrisprSaveToSession, { loading: dataload }] =
    useCustomCrisprSaveToSessionMutation()
  const saveSessionDataToSession = async (input, productInput) => {
    await customCrisprSaveToSession({ variables: { input, productInput } })
  }

  const [customCrisprTotalValidationMutation, { loading: validload }] =
    useCustomCrisprTotalValidationMutation()

  const totalValidationCall = async (
    obj: SessionData[],
    checkForDefault?: SpecificationMessages
  ) => {
    delete obj[0].listCompanionProducts
    const response = await customCrisprTotalValidationMutation({
      variables: { input: obj, productInput: productDetails },
    })

    let validationResp
    if (response?.data?.customCrisprTotalValidation)
      validationResp = response?.data?.customCrisprTotalValidation[0]
    if (
      response?.data?.customCrisprTotalValidation &&
      !validationResp.serverError
    ) {
      dispatch({
        type: 'SET_PAGE_ERROR',
        data: '',
      })
      const validationResponse = cloneDeep(
        response?.data?.customCrisprTotalValidation
      )
      delete validationResponse[0].__typename
      delete validationResponse[0].serverError
      if (validationResponse[0].listSubModels) {
        validationResponse[0].listSubModels.forEach((model) => {
          if (model && model.__typename) {
            delete model?.__typename
          }
          if (model && model.listSubModelErrors) {
            model.listSubModelErrors = []
          }
        })
      }
      if (validationResponse[0].listError) {
        validationResponse[0].listError.forEach((error) => {
          if (error && error?.__typename) delete error?.__typename
        })
      }
      if (validationResponse[0].listCompanionProducts) {
        const companionProducts: any[] = []
        validationResponse[0].listCompanionProducts.forEach((item) => {
          if (item) {
            companionProducts.push(item.key)
          }
        })
        dispatch({
          type: 'SET_COMPANION_PRODUCT_INFORMATION',
          data: companionProducts,
        })
        delete validationResponse[0].listCompanionProducts
      }
      if (
        sessionData[0].physicalMaterial === SpecificationMessages.SRNA &&
        validationResponse[0]?.listFormat
      ) {
        validationResponse[0].format = validationResponse[0]?.listFormat[0]
      }
      delete validationResponse[0].listFormat
      await saveSessionDataToSession(sessionData, productDetails)
      //on modifcation change, check for defult value of purification
      if (
        checkForDefault &&
        checkForDefault === SpecificationMessages.modification &&
        validationResponse[0]?.listPurification &&
        validationResponse[0]?.listPurification.length === 1
      ) {
        validationResponse[0].purification =
          validationResponse[0]?.listPurification[0]
        setReqPuri(false)
        if (
          validationResponse[0]?.species !== '' &&
          validationResponse[0]?.scale !== '' &&
          validationResponse[0]?.modification !== '' &&
          validationResponse[0]?.purification !== '' &&
          validationResponse[0]?.structure !== ''
        ) {
          setOpen(false)
        }
      }

      dispatch({
        type: 'UPDATE_INPUT',
        data: validationResponse,
      })
    } else {
      dispatch({
        type: 'SET_PAGE_ERROR',
        data: ConfiguratorValues.InternalServerError,
      })
      window.scrollTo(0, 0)
    }
  }

  const [getCrisprPriceCall, { data, loading: priceload }] =
    useGetCrisprPriceCallLazyQuery({
      fetchPolicy: 'network-only',
    })
  const fetchPriceData = async (obj) => {
    //check if emptyObj there
    let isEmptyObj = false
    sessionData[0].listSubModels.forEach((listModel) => {
      if (
        sessionData[0].listSubModels.length > 1 &&
        listModel.isCurrentForm &&
        listModel.name === '' &&
        listModel.sequence === ''
      ) {
        isEmptyObj = true
      }
    })
    //remove emtpy obj
    if (isEmptyObj) {
      sessionData[0].listSubModels.splice(-1, 1)
      dispatch({
        type: 'UPDATE_CURRENT_INDEX',
        data: currentSubModelIndex - 1,
      })
    }
    if (!isEmptyObj) {
      sessionData[0].listSubModels[currentSubModelIndex].isCurrentForm = false
    }
    await saveSessionDataToSession(sessionData, productDetails)
    await getCrisprPriceCall({
      variables: { product: obj },
    })
  }

  React.useEffect(() => {
    if (sessionData[0].physicalMaterial === SpecificationMessages.VIRUS) {
      totalValidationCall(state.sessionData)
    }
  }, [sessionData[index].aliquoting])

  React.useEffect(() => {
    if (sessionData[0].physicalMaterial) {
      const physicalMaterial: any = sessionData[index].physicalMaterial
      setPhysicalMaterial(physicalMaterial)
    }
  }, [sessionData[index].physicalMaterial])

  React.useEffect(() => {
    if (
      state.sessionData[0].cartId &&
      state.sessionData[0].physicalMaterial !== '' &&
      !state.sessionData[0].listPhysicalMaterials
    ) {
      totalValidationCall(state.sessionData)
    }
  }, [])

  React.useEffect(() => {
    window.scrollTo(0, 0)
    const physicalMaterial: any = sessionData[index].physicalMaterial
    if (physicalMaterial === '') {
      setPhysicalMaterial(SpecificationMessages.Select)
    }
    const saveVectorValue: any = sessionData[index].vector
    if (saveVectorValue) {
      setVectorDropdownValue(saveVectorValue)
    } else {
      setVectorDropdownValue(SpecificationMessages.lv01)
    }
    const saveAmountValue: any = sessionData[index].amount
    if (saveAmountValue) {
      setAmountDropdownValue(saveAmountValue)
    } else {
      setAmountDropdownValue(SpecificationMessages.one)
    }
    const saveLentiviralBackboneValue: any =
      sessionData[index].lentiviralBackbone
    if (saveLentiviralBackboneValue) {
      setLentiviralDropdownValue(saveLentiviralBackboneValue)
    } else {
      setLentiviralDropdownValue(SpecificationMessages.lv01)
    }
    const saveViralTiterValue: any = sessionData[index].viralTiter
    if (saveViralTiterValue) {
      setTiterDropdownValue(saveViralTiterValue)
    } else {
      setTiterDropdownValue(SpecificationMessages.TenPSix)
    }
    setVolumeFlag(true)
    const saveVolumeValue: any = sessionData[index].volume
    if (saveVolumeValue) {
      setVolumeDropdownValue(saveVolumeValue)
    } else {
      setVolumeFlag(false)
    }
    setAliquotingFlag(true)
    const saveAliquotingValue: any = sessionData[index].aliquoting
    if (saveAliquotingValue) {
      setAliquotingDropdownValue(saveAliquotingValue)
    } else {
      setAliquotingFlag(false)
    }
    if (
      physicalMaterial === SpecificationMessages.SRNA &&
      sessionData[index].structure === ''
    ) {
      setReqStructure(true)
      setOpen(true)
    }
    if (
      physicalMaterial === SpecificationMessages.SRNA &&
      sessionData[index].scale === ''
    ) {
      setReqScale(true)
      setOpen(true)
    }
    if (
      physicalMaterial === SpecificationMessages.SRNA &&
      sessionData[index].modification === ''
    ) {
      setReqMod(true)
      setOpen(true)
    }
    if (
      physicalMaterial === SpecificationMessages.SRNA &&
      sessionData[index].purification === ''
    ) {
      setReqPuri(true)
      setOpen(true)
    }

    specificationsData.specifications.forEach((specificationList) => {
      if (
        specificationList.categoryName === SpecificationMessages.Structure &&
        specificationList.specificationModifications.length > 1
      ) {
        setCheckForRadioButton(true)
      } else if (
        specificationList.categoryName === SpecificationMessages.Structure &&
        specificationList.specificationModifications.length === 1
      ) {
        setCheckForRadioButton(false)
      }
    })

    // TODO: need a test before we refactor these dependencies
  }, [])

  const setInitialState = () => {
    return {
      name: '',
      geneId: '',
      geneSymbol: '',
      sequence: '',
      id: customCrisprIndex,
      isOverridden: false,
      baseCount: 0,
      isCurrentForm: true,
      price: '',
      cartId: '',
      itemId: '',
    }
  }

  React.useEffect(() => {
    if (data) {
      if (!data.getCrisprPriceCall?.serverError) {
        dispatch({
          type: 'SET_PAGE_ERROR',
          data: '',
        })
        setItemDetails(data.getCrisprPriceCall?.response)
        if (data.getCrisprPriceCall?.response) {
          let isEmptyObj = false
          const priceCallResponse = data.getCrisprPriceCall?.response[0]
          const listSubModels = priceCallResponse?.listSubModels
          if (listSubModels) {
            const listSubmodelPrice = listSubModels[0]?.price
            sessionData[0].listSubModels.forEach((listSubmodel) => {
              if (listSubmodelPrice) {
                listSubmodel.price = listSubmodelPrice
              }
              if (
                sessionData[0].listSubModels.length > 1 &&
                listSubmodel.isCurrentForm &&
                listSubmodel.name === '' &&
                listSubmodel.sequence === ''
              ) {
                isEmptyObj = true
              }
            })
          }
          sessionData[0].price = priceCallResponse?.price
          sessionData[0].currencySymbol = priceCallResponse?.currencySymbol
          sessionData[0].currency = priceCallResponse?.currency
          if (!isEmptyObj) {
            const emptyStateObj = Object.assign({}, setInitialState())
            if (sessionData[0] && sessionData[0].listSubModels) {
              sessionData[0].listSubModels.push(emptyStateObj)
            }
            dispatch({
              type: 'UPDATE_CURRENT_INDEX',
              data: currentSubModelIndex + 1,
            })
          }
          //updating pageNo to enable side bar for 'Review & Confirm' Page
          sessionData[0].pageNo = 2
          dispatch({ type: 'SET_STEP_NO', data: 2 })
          dispatch({
            type: 'UPDATE_INPUT',
            data: [...state.sessionData],
          })
          saveSessionDataToSession(sessionData, productDetails)
        }
        router.push(reviewPageRoute)
      } else {
        dispatch({
          type: 'SET_PAGE_ERROR',
          data: ConfiguratorValues.InternalServerError,
        })
        window.scrollTo(0, 0)
      }
    }
    // TODO: need a test before we refactor these dependencies
  }, [data])

  const getValue = (event, keys) => {
    setLoader(false)
    const { value } = event.target

    if (keys === SpecificationMessages.species) {
      sessionData[index].structure = ''
      sessionData[index].scale = ''
      setReqSpecies(false)
      sessionData[index].species = SpecificationMessages.SPCAS9
      if (
        sessionData[index].physicalMaterial === SpecificationMessages.SRNA &&
        value === SpecificationMessages.FNCAS9
      ) {
        setReqStructure(false)
        sessionData[index].structure = SpecificationMessages.CRRNA
      }
    } else if (keys === SpecificationMessages.Structure) {
      sessionData[index].scale = ''
      setReqStructure(false)
      sessionData[index].structure = value
      if (
        sessionData[index].physicalMaterial === SpecificationMessages.SRNA &&
        value === SpecificationMessages.SYGRNA
      ) {
        setReqScale(false)
        sessionData[index].scale = SpecificationMessages.Three
      }
      sessionData[index].modification = ''
      sessionData[index].purification = ''
    } else if (keys === SpecificationMessages.scale) {
      setReqScale(false)
      sessionData[index].scale = value
    } else if (keys === SpecificationMessages.modification) {
      sessionData[index].purification = ''
      setReqMod(false)
      sessionData[index].modification = value
    } else if (keys === SpecificationMessages.Purification) {
      setReqPuri(false)
      sessionData[index].purification = value
    }
    if (
      sessionData[index].species !== '' &&
      sessionData[index].scale !== '' &&
      sessionData[index].modification !== '' &&
      sessionData[index].purification !== '' &&
      sessionData[index].structure !== ''
    ) {
      setOpen(false)
    }
    //updating pageNo to enable side bar for 'Specifications' Page
    sessionData[index].pageNo = 1
    dispatch({
      type: 'UPDATE_INPUT',
      data: [...state.sessionData],
    })
    if (state.stepNo !== 1) {
      dispatch({ type: 'SET_STEP_NO', data: 1 })
    }
    totalValidationCall(state.sessionData, keys)
  }

  interface ModificationDropDownData {
    label: string
    value: string
  }

  const modificationDropDownData: ModificationDropDownData = {
    label: '',
    value: '',
  }

  const dropDownValuesForCustomCrisprForAliq = (
    categoryType: string,
    specificationsData,
    dropDownValuesFromResponse
  ) => {
    const dropDownList = [modificationDropDownData]

    dropDownList.length = 0
    if (specificationsData && dropDownValuesFromResponse) {
      specificationsData.specifications.forEach((specifications) => {
        if (specifications.categoryName === SpecificationMessages.aliquoting) {
          for (const dropDownValue in dropDownValuesFromResponse) {
            specifications.specificationModifications.forEach((category) => {
              if (
                category.value === dropDownValuesFromResponse[dropDownValue]
              ) {
                modificationDropDownData.label =
                  messages[category.displayName]?.defaultMessage ||
                  category.displayName
                modificationDropDownData.value = category.value
                dropDownList.push({ ...modificationDropDownData })
              }
            })
            if (!aliquotingFlag) {
              const aliqValue = dropDownList[0]
              if (aliqValue) {
                setAliquotingDropdownValue(Object.values(aliqValue)[1])
                sessionData[index].aliquoting = Object.values(aliqValue)[1]
              }
            }
          }
        }
      })
    }
    return dropDownList
  }

  const dropDownValuesForCustomCrisprForVol = (
    categoryType: string,
    specificationsData,
    dropDownValuesFromResponse
  ) => {
    const dropDownList = [modificationDropDownData]

    dropDownList.length = 0
    if (specificationsData && dropDownValuesFromResponse) {
      specificationsData.specifications.forEach((specifications) => {
        if (specifications.categoryName === SpecificationMessages.volume) {
          for (const dropDownValue in dropDownValuesFromResponse) {
            specifications.specificationModifications.forEach((category) => {
              if (
                category.value === dropDownValuesFromResponse[dropDownValue]
              ) {
                modificationDropDownData.label =
                  messages[category.displayName]?.defaultMessage ||
                  category.displayName
                modificationDropDownData.value = category.value
                dropDownList.push({ ...modificationDropDownData })
              }
            })
            if (!volumeFlag && !sessionData[index].volume) {
              const volValue = dropDownList[0]
              if (volValue) {
                setVolumeDropdownValue(Object.values(volValue)[1])
                sessionData[index].volume = Object.values(volValue)[1]
              }
            }
          }
        }
      })
    }
    return dropDownList
  }

  const disablingRadioButton = (data, categoryName) => {
    if (sessionData[index]) {
      let list: any = []

      switch (categoryName) {
        case SpecificationMessages.species: {
          list = sessionData[index].listSpecies

          break
        }

        case SpecificationMessages.Structure: {
          list = sessionData[index].listStructure

          break
        }
        case SpecificationMessages.scale: {
          list = sessionData[index].listScale

          break
        }
        case SpecificationMessages.Purification: {
          list = sessionData[index].listPurification
          break
        }
        default:
          return false
      }
      if (data.value) {
        if (list === null) {
          return true
        } else if (list.includes(data.value)) {
          return false
        }
      }
    }
    return true
  }

  const defaultRadioButton = (data, specificationType) => {
    if (specificationType === SpecificationMessages.species) {
      return SpecificationMessages.SPCAS9
    }
    return sessionData[0][specificationType]
  }

  const specificationData = (specificationsData, specificationType) => {
    let specificationModifications =
      specificationsData.specificationModifications
    if (specificationType === SpecificationMessages.Purification) {
      specificationModifications =
        specificationsData.specificationModifications.filter((modification) => {
          return !disablingRadioButton(modification, specificationType)
        })
    }
    return specificationModifications.map((specification, index) => {
      return (
        <FormControlLabel
          key={index}
          value={specification.value}
          control={
            <StyledRadio
              className={classes.radio}
              onChange={(e) => getValue(e, specificationType)}
              isConfiguratorsRedesign="true"
            />
          }
          className={classes.labelFont}
          label={
            messages[specification.displayName]?.defaultMessage ||
            specification.displayName
          }
          disabled={
            specificationType === SpecificationMessages.modification
              ? false
              : disablingRadioButton(specification, specificationType)
          }
        />
      )
    })
  }

  const populateSpecification = (specificationType, specificationsData) => {
    return specificationsData.specifications.map((specificationList, index) => {
      if (
        specificationList.categoryName === specificationType &&
        specificationList.specificationModifications.length > 1
      ) {
        return (
          <RadioGroup
            aria-label="radio"
            name="radio"
            key={index}
            value={defaultRadioButton(specificationList, specificationType)}
            className={classes.radioGroup}
          >
            {specificationData(specificationList, specificationType)}
          </RadioGroup>
        )
      } else if (
        specificationList.categoryName === specificationType &&
        specificationList.specificationModifications.length === 1
      ) {
        if (specificationType === SpecificationMessages.species) {
          return (
            <RadioGroup
              aria-label="radio"
              name="radio"
              key={index}
              value={defaultRadioButton(specificationList, specificationType)}
              className={classes.radioGroup}
            >
              {specificationData(specificationList, specificationType)}
            </RadioGroup>
          )
        }
        if (specificationType === SpecificationMessages.Structure) {
          sessionData[0].structure = SpecificationMessages.CRRNA
          return specificationList.specificationModifications.map(
            (specification) => {
              return intl.formatMessage(
                checkValidKey(
                  messages[specification.displayName],
                  specification.displayName
                )
              )
            }
          )
        }
      }
      return null
    })
  }

  const onPhysicalMaterialChange = (event) => {
    setLoader(true)
    setReqPhysical(false)
    setOpen(false)
    const { value } = event.target
    setPhysicalMaterial(value)
    sessionData[index].physicalMaterial = value
    sessionData[index].pageNo = 2
    if (value === SpecificationMessages.VIRUS) {
      sessionData[index].viralTiter = SpecificationMessages.TenPSix
      sessionData[index].lentiviralBackbone = SpecificationMessages.lv01
      sessionData[index].aliquoting = SpecificationMessages.mul1x200
      sessionData[index].volume = SpecificationMessages.dotTwo
      sessionData[index].vector = ''
      sessionData[index].amount = ''
      sessionData[index].species = ''
      sessionData[index].scale = ''
      sessionData[index].modification = ''
      sessionData[index].purification = ''
      sessionData[index].structure = ''
      sessionData[index].format = ''
      setReqSpecies(false)
      setReqPuri(false)
      setReqScale(false)
      setReqStructure(false)
      setReqMod(false)
    } else if (value === SpecificationMessages.SRNA) {
      sessionData[index].vector = ''
      sessionData[index].amount = ''
      sessionData[index].viralTiter = ''
      sessionData[index].lentiviralBackbone = ''
      sessionData[index].aliquoting = ''
      sessionData[index].volume = ''
      sessionData[index].modification = ''
      sessionData[index].purification = ''
      sessionData[index].structure = ''
      sessionData[index].scale = ''
      sessionData[index].species = SpecificationMessages.SPCAS9
      setReqSpecies(false)
    } else if (value === SpecificationMessages.DNA) {
      sessionData[index].vector = SpecificationMessages.lv01
      sessionData[index].amount = SpecificationMessages.one
      sessionData[index].viralTiter = ''
      sessionData[index].lentiviralBackbone = ''
      sessionData[index].aliquoting = ''
      sessionData[index].volume = ''
      sessionData[index].species = ''
      sessionData[index].scale = ''
      sessionData[index].format = ''
      sessionData[index].modification = ''
      sessionData[index].purification = ''
      sessionData[index].structure = ''
      setReqSpecies(false)
      setReqPuri(false)
      setReqScale(false)
      setReqStructure(false)
      setReqMod(false)
    } else if (value === SpecificationMessages.VITRO) {
      sessionData[index].vector = ''
      sessionData[index].amount = ''
      sessionData[index].species = ''
      sessionData[index].scale = ''
      sessionData[index].format = ''
      sessionData[index].modification = ''
      sessionData[index].purification = ''
      sessionData[index].structure = ''
      sessionData[index].viralTiter = ''
      sessionData[index].lentiviralBackbone = ''
      sessionData[index].aliquoting = ''
      sessionData[index].volume = ''
      setReqSpecies(false)
      setReqPuri(false)
      setReqScale(false)
      setReqStructure(false)
      setReqMod(false)
    }
    //updating pageNo to enable side bar for 'Specifications' Page
    sessionData[index].pageNo = 1
    dispatch({
      type: 'UPDATE_INPUT',
      data: [...state.sessionData],
    })

    if (state.stepNo !== 1) {
      dispatch({ type: 'SET_STEP_NO', data: 1 })
    }
    totalValidationCall(state.sessionData)
  }

  const onVectorsDropdownChange = (event) => {
    setLoader(false)
    const { value } = event.target
    setVectorDropdownValue(value)
    sessionData[index].vector = value
    //updating pageNo to enable side bar for 'Specifications' Page
    sessionData[index].pageNo = 1
    dispatch({
      type: 'UPDATE_INPUT',
      data: [...state.sessionData],
    })
    if (state.stepNo !== 1) {
      dispatch({ type: 'SET_STEP_NO', data: 1 })
    }
    totalValidationCall(state.sessionData)
  }

  const onAmountDropdownChange = (event) => {
    setLoader(false)
    const { value } = event.target
    setAmountDropdownValue(value)
    sessionData[index].amount = value
    //updating pageNo to enable side bar for 'Specifications' Page
    sessionData[index].pageNo = 1
    dispatch({
      type: 'UPDATE_INPUT',
      data: [...state.sessionData],
    })
    if (state.stepNo !== 1) {
      dispatch({ type: 'SET_STEP_NO', data: 1 })
    }
    totalValidationCall(state.sessionData)
  }

  const onLentiviralDropdownChange = (event) => {
    setLoader(false)
    const { value } = event.target
    setLentiviralDropdownValue(value)
    //updating pageNo to enable side bar for 'Specifications' Page
    sessionData[index].pageNo = 1
    dispatch({
      type: 'UPDATE_INPUT',
      data: [...state.sessionData],
    })
    sessionData[index].lentiviralBackbone = value
    if (state.stepNo !== 1) {
      dispatch({ type: 'SET_STEP_NO', data: 1 })
    }
    totalValidationCall(state.sessionData)
  }

  const onTiterDropdownChange = (event) => {
    setLoader(false)
    const { value } = event.target
    setTiterDropdownValue(value)
    sessionData[index].viralTiter = value
    //updating pageNo to enable side bar for 'Specifications' Page
    sessionData[index].pageNo = 1
    dispatch({
      type: 'UPDATE_INPUT',
      data: [...state.sessionData],
    })
    setAliquotingFlag(false)
    setVolumeFlag(false)
    if (state.stepNo !== 1) {
      dispatch({ type: 'SET_STEP_NO', data: 1 })
    }
    totalValidationCall(state.sessionData)
  }

  const onVolumeDropdownChange = (event) => {
    setLoader(false)
    const { value } = event.target
    setVolumeDropdownValue(value)
    sessionData[index].volume = value
    //updating pageNo to enable side bar for 'Specifications' Page
    sessionData[index].pageNo = 1
    dispatch({
      type: 'UPDATE_INPUT',
      data: [...state.sessionData],
    })
    setAliquotingFlag(false)
    setVolumeFlag(true)
    if (state.stepNo !== 1) {
      dispatch({ type: 'SET_STEP_NO', data: 1 })
    }
    totalValidationCall(state.sessionData)
  }

  const onAliquotingDropdownChange = (event) => {
    setLoader(false)
    const { value } = event.target
    setAliquotingDropdownValue(value)
    sessionData[index].aliquoting = value
    //updating pageNo to enable side bar for 'Specifications' Page
    sessionData[index].pageNo = 1
    dispatch({
      type: 'UPDATE_INPUT',
      data: [...state.sessionData],
    })
    setAliquotingFlag(true)
    if (state.stepNo !== 1) {
      dispatch({ type: 'SET_STEP_NO', data: 1 })
    }
    totalValidationCall(state.sessionData)
  }

  const nextButton = () => {
    if (physicalMaterial === SpecificationMessages.Select) {
      setReqPhysical(true)
      setOpen(true)
    }
    if (
      physicalMaterial === SpecificationMessages.SRNA &&
      sessionData[index].species === ''
    ) {
      setReqSpecies(true)
      setOpen(true)
    }
    if (
      physicalMaterial === SpecificationMessages.SRNA &&
      sessionData[index].structure === ''
    ) {
      setReqStructure(true)
      setOpen(true)
    }
    if (
      physicalMaterial === SpecificationMessages.SRNA &&
      sessionData[index].scale === ''
    ) {
      setReqScale(true)
      setOpen(true)
    }
    if (
      physicalMaterial === SpecificationMessages.SRNA &&
      sessionData[index].modification === ''
    ) {
      setReqMod(true)
      setOpen(true)
    }
    if (
      physicalMaterial === SpecificationMessages.SRNA &&
      sessionData[index].purification === ''
    ) {
      setReqPuri(true)
      setOpen(true)
    }

    if (
      (physicalMaterial === SpecificationMessages.SRNA &&
        sessionData[index].species !== '' &&
        sessionData[index].scale !== '' &&
        sessionData[index].modification !== '' &&
        sessionData[index].purification !== '' &&
        sessionData[index].structure !== '') ||
      physicalMaterial === SpecificationMessages.DNA ||
      physicalMaterial === SpecificationMessages.VIRUS ||
      physicalMaterial === SpecificationMessages.VITRO
    ) {
      setOpen(false)
      fetchPriceData(productDetails)
    } else {
      window.scrollTo(0, 0)
    }
  }
  const vectorURL =
    '/technical-documents/technical-article/genomics/advanced-gene-editing/crispr-clone-control-and-cas9-essentials#Lentiviral%20vectors'

  const url =
    '/technical-documents/technical-article/genomics/advanced-gene-editing/crispr-clone-control-and-cas9-essentials#Lentiviral%20vectors'

  const requiredError = (
    <div className={classes.requiredFieldError}>
      <ErrorIcon className={classes.errorIcon} />
      <FormattedMessage {...messages.CONFIG_REQUIRED} />
    </div>
  )

  return (
    <>
      <div className={classes.container}>
        <Box className={classes.pageHeader}>
          <Typography variant="h2" component="h2" className={classes.title}>
            <FormattedMessage {...messages.CONF_SPECIFICATIONS_crisprgrna} />
          </Typography>
          <PerformanceGuaranteeModal />
        </Box>
        {open ? (
          <GlobalAlert severity="error" className={classes.errorAlert}>
            <FormattedMessage {...messages.CONF_DNAPLATES_ERROR_STEP2} />
          </GlobalAlert>
        ) : null}

        <Box mt={6} className={classes.font16}>
          <FormattedMessage
            {...messages.CONFIG_VISIT_CRISPR_ESSENTIALS_PAGE_MORE_DETAILS}
            values={{
              link: (
                <Link href={url} target="_blank">
                  <FormattedMessage {...messages.CONF_CRISPR_ESSENTIALS_PAGE} />
                </Link>
              ),
            }}
          />
        </Box>
        <div className={classes.requiredField}>
          {'*'}
          <FormattedMessage {...messages.CONFIG_REQUIRED_FIELDS} />
        </div>
        <Formik
          initialValues={{
            materialName: (
              <FormattedMessage
                {...messages.CONF_SPECIFICATION_PHYSICAL_MATERIAL_SYNTHETIC_RNA_crisprgrna}
              />
            ),
          }}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              actions.setSubmitting(false)
            }, 1000)
          }}
        >
          {() => (
            <Grid container direction="column">
              <Grid item sm={12} md={4} className={classes.marginTop24}>
                <Field
                  name="materialName"
                  component={LiquidSelectAdapter}
                  label={
                    <span
                      className={clsx(
                        classes.fieldText,
                        reqPhysical && classes.hasRequiredError
                      )}
                    >
                      *&nbsp;
                      <FormattedMessage
                        {...messages.CONF_SPECIFICATION_PHYSICAL_MATERIAL}
                      />
                    </span>
                  }
                  onChange={onPhysicalMaterialChange}
                  value={physicalMaterial}
                  options={[
                    {
                      label: SpecificationMessages.Select,
                      value: SpecificationMessages.Select,
                    },
                    ...dropDownValuesForCustomCrispr(
                      'physicalMaterial',
                      specificationsData,
                      state.sessionData[0].listPhysicalMaterials
                    ),
                  ]}
                  rootClass={classes.dropDown}
                />
                {reqPhysical && requiredError}
              </Grid>

              {physicalMaterial === SpecificationMessages.SRNA ? (
                (dataload || validload) && loader ? (
                  ''
                ) : (
                  <>
                    <Box mt={4}>
                      <CustomCrisprRecommendationModal />
                    </Box>
                    <Grid item md={4} sm={12} className={classes.marginTop24}>
                      <span
                        className={clsx(
                          classes.fieldText,
                          reqSpecies && classes.hasRequiredError
                        )}
                      >
                        *&nbsp;
                        <FormattedMessage
                          {...messages.CONF_SPECIFICATION_CRISPR_SPECIES_crisprgrna}
                        />
                      </span>
                      <Box>
                        {populateSpecification('species', specificationsData)}
                      </Box>

                      {reqSpecies && requiredError}
                    </Grid>

                    <Grid
                      item
                      md={4}
                      sm={12}
                      className={clsx(
                        classes.marginTop24,
                        checkForRadioButton &&
                          reqStructure &&
                          classes.hasRequiredError
                      )}
                    >
                      <span className={classes.fieldText}>
                        *&nbsp;
                        <FormattedMessage
                          {...messages.CONF_SPECIFICATION_STRUCTURE_crisprgrna}
                        />
                      </span>
                      {
                        <>
                          <div>
                            {populateSpecification(
                              SpecificationMessages.Structure,
                              specificationsData
                            )}
                          </div>
                          {checkForRadioButton && reqStructure && requiredError}
                        </>
                      }
                    </Grid>

                    <Grid
                      item
                      sm={12}
                      md={3}
                      className={clsx(
                        classes.marginTop24,
                        reqScale && classes.hasRequiredError
                      )}
                    >
                      <span className={classes.fieldText}>
                        *&nbsp;
                        <FormattedMessage
                          {...messages.CONF_SPECIFICATION_SYNTHESIS_SCALE_crisprgrna}
                        />
                      </span>
                      <div>
                        {populateSpecification('scale', specificationsData)}
                      </div>
                      {reqScale && requiredError}
                    </Grid>

                    <Grid
                      item
                      sm={12}
                      md={4}
                      className={clsx(
                        classes.marginTop24,
                        reqMod && classes.hasRequiredError
                      )}
                    >
                      <div className={classes.fieldText}>
                        *&nbsp;
                        <FormattedMessage
                          {...messages.CONF_SPECIFICATION_MODIFIED_crisprgrna}
                        />
                        <Tooltip
                          title={
                            <FormattedMessage
                              {...messages.CONF_SPECIFICATION_MODIFICATION_HELP_TEXT_customcrispr}
                            />
                          }
                          classes={{
                            tooltip: classes.InfoPopup,
                            arrow: classes.arrow,
                          }}
                          arrow
                          placement="bottom-start"
                        >
                          <InfoIcon
                            color="primary"
                            className={classes.InfoIcon}
                          />
                        </Tooltip>
                      </div>

                      <div>
                        {populateSpecification(
                          'modification',
                          specificationsData
                        )}
                      </div>
                      {reqMod && requiredError}
                    </Grid>

                    {sessionData[index].structure !== '' ? (
                      <Grid
                        item
                        sm={12}
                        md={4}
                        className={clsx(
                          classes.marginTop24,
                          reqPuri &&
                            sessionData[index].structure !== '' &&
                            classes.hasRequiredError
                        )}
                      >
                        <div className={classes.fieldText}>
                          *&nbsp;
                          <FormattedMessage
                            {...messages.CONF_SPECIFICATION_PURIFICATION_crisprgrna}
                          />
                          <Tooltip
                            title={
                              <>
                                <Typography
                                  variant="body2"
                                  className={classes.purificationHelpText}
                                >
                                  <FormattedMessage
                                    {...messages.PURIFICATION_HELP_TEXT_ONE}
                                  />
                                </Typography>
                                <Typography variant="body2">
                                  <FormattedMessage
                                    {...messages.PURIFICATION_HELP_TEXT_TWO}
                                  />
                                </Typography>
                              </>
                            }
                            classes={{
                              tooltip: classes.InfoPopup,
                              arrow: classes.arrow,
                            }}
                            arrow
                            placement="bottom-start"
                          >
                            <InfoIcon
                              color="primary"
                              className={classes.InfoIcon}
                            />
                          </Tooltip>
                        </div>
                        <div>
                          {populateSpecification(
                            'purification',
                            specificationsData
                          )}
                        </div>
                        {reqPuri &&
                          sessionData[index].structure !== '' &&
                          requiredError}
                      </Grid>
                    ) : (
                      ''
                    )}

                    <Grid item className={classes.marginTop24}>
                      <span className={classes.fieldText}>
                        <FormattedMessage
                          {...messages.CONF_SPECIFICATION_FORMAT_crisprgrna}
                        />
                      </span>
                      <div className={classes.dry}>
                        <FormattedMessage
                          {...messages.CONF_SPECIFICATION_DRY_crisprgrna}
                        />
                      </div>
                    </Grid>
                  </>
                )
              ) : physicalMaterial === SpecificationMessages.DNA ? (
                (dataload || validload) && loader ? (
                  ''
                ) : (
                  <>
                    <Grid item sm={12} md={4} className={classes.marginTop24}>
                      <Field
                        name="vector"
                        label={
                          <span className={classes.fieldText}>
                            *&nbsp;
                            <FormattedMessage
                              {...messages.CONF_DNA_SPECIFICATION_crispgrna}
                            />
                          </span>
                        }
                        component={LiquidSelectAdapter}
                        value={vectorDropdownValue}
                        onChange={onVectorsDropdownChange}
                        options={[
                          ...dropDownValuesForCustomCrispr(
                            'vector',
                            specificationsData,
                            state.sessionData[0].listVectors
                          ),
                        ]}
                        rootClass={classes.dropDown}
                      />
                    </Grid>

                    <Grid item sm={12} md={4} className={classes.marginTop24}>
                      <Field
                        name="amount"
                        component={LiquidSelectAdapter}
                        label={
                          <span className={classes.fieldText}>
                            *&nbsp;
                            <FormattedMessage
                              {...messages.CONF_AMOUNT_DNA_SPECIFICATION_crispgrna}
                            />
                          </span>
                        }
                        value={amountDropdownValue}
                        onChange={onAmountDropdownChange}
                        options={[
                          ...dropDownValuesForCustomCrispr(
                            'amount',
                            specificationsData,
                            state.sessionData[0].listAmount
                          ),
                        ]}
                        rootClass={classes.dropDown}
                      />
                    </Grid>
                  </>
                )
              ) : physicalMaterial === SpecificationMessages.VIRUS ? (
                (dataload || validload) && loader ? (
                  ''
                ) : (
                  <>
                    <Grid item sm={12} md={4} className={classes.marginTop24}>
                      <Field
                        name="lentivirus"
                        component={LiquidSelectAdapter}
                        value={lentiviralDropdownValue}
                        onChange={onLentiviralDropdownChange}
                        options={[
                          ...dropDownValuesForCustomCrispr(
                            'lentiviralBackbone',
                            specificationsData,
                            state.sessionData[0].listLentiviralBackbones
                          ),
                        ]}
                        className={classes.vectorLabel2}
                        rootClass={classes.dropDown}
                        label={
                          <span className={classes.fieldText}>
                            *&nbsp;
                            <FormattedMessage
                              {...messages.CONF_SPECIFICATION_LENTIVIRAL_BACKBONE}
                            />
                            <Tooltip
                              title={
                                <div>
                                  <span className={classes.vectorLabel}>
                                    <FormattedMessage
                                      {...messages.CONF_DNA_DROPDOWN_SPECIFICATION_crispgrna}
                                    />
                                  </span>
                                  <Link
                                    href={vectorURL}
                                    className={classes.linkStyle3}
                                    target="_blank"
                                  >
                                    <FormattedMessage
                                      {...messages.CONF_DNA_LINK_SPECIFICATION_crispgrna}
                                    />
                                  </Link>
                                </div>
                              }
                              placement="bottom-end"
                              classes={{
                                tooltip: classes.InfoPopup2,
                                arrow: classes.arrow,
                              }}
                              arrow
                              interactive
                            >
                              <InfoIcon
                                color="primary"
                                aria-label="info"
                                className={classes.InfoIcon}
                              ></InfoIcon>
                            </Tooltip>
                          </span>
                        }
                      />
                    </Grid>

                    <Grid item sm={12} md={4} className={classes.marginTop24}>
                      <Field
                        name="titerField"
                        component={LiquidSelectAdapter}
                        label={
                          <span className={classes.fieldText}>
                            *&nbsp;
                            <FormattedMessage
                              {...messages.CONF_SPEC_VIRAL_TITER}
                            />
                          </span>
                        }
                        value={titerDropdownValue}
                        onChange={onTiterDropdownChange}
                        options={[
                          ...dropDownValuesForCustomCrispr(
                            'viralTiter',
                            specificationsData,
                            state.sessionData[0].listViralTiters
                          ),
                        ]}
                        rootClass={classes.dropDown}
                      />
                    </Grid>

                    <Grid item sm={12} md={4} className={classes.marginTop24}>
                      <Field
                        name="volume"
                        component={LiquidSelectAdapter}
                        label={
                          <span className={classes.fieldText}>
                            *&nbsp;
                            <FormattedMessage
                              {...messages.CONF_VIRUS_VOLUME_SPECIFICATION_crispgrna}
                            />
                          </span>
                        }
                        value={volumeDropdownValue}
                        onChange={onVolumeDropdownChange}
                        options={[
                          ...dropDownValuesForCustomCrisprForVol(
                            SpecificationMessages.volume,
                            specificationsData,
                            state.sessionData[0].listVolumes
                          ),
                        ]}
                        rootClass={classes.dropDown}
                      />
                    </Grid>
                    <Grid item sm={12} md={4} className={classes.marginTop24}>
                      <Field
                        name="aliquoting"
                        component={LiquidSelectAdapter}
                        label={
                          <span className={classes.fieldText}>
                            *&nbsp;
                            <FormattedMessage
                              {...messages.CONF_VIRUS_ALIQUOTING_SPECIFICATION_crispgrna}
                            />
                          </span>
                        }
                        value={aliquotingDropdownValue}
                        onChange={onAliquotingDropdownChange}
                        options={[
                          ...dropDownValuesForCustomCrisprForAliq(
                            'aliquoting',
                            specificationsData,
                            state.sessionData[0].listAliquotings
                          ),
                        ]}
                        rootClass={classes.dropDown}
                      />
                    </Grid>
                  </>
                )
              ) : (
                ''
              )}
            </Grid>
          )}
        </Formik>
      </div>
      <Box textAlign="end" mt={6}>
        <Button
          id="customCrispr-nextStep-button"
          variant="contained"
          size="medium"
          color="primary"
          onClick={nextButton}
          className={classes.nextButton}
        >
          <FormattedMessage {...messages.CONFIG_NEXT_STEP} />
        </Button>
      </Box>
      {(dataload || validload || priceload) && <CircleLoader />}
    </>
  )
}

export default CrisprSpecifications
